import { Link } from "react-router-dom";
import type { Device } from "../../api/types";
import { useAlerts, useDeviceMetrics } from "../../hooks/queries";
import { formatMetricValue, metricLabel } from "../../hooks/metrics";
import {
  AlertStateBadge,
  DeviceStatusBadge,
  OsBadge,
  SeverityBadge,
} from "../../components/domain/Badges";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import { Skeleton } from "../../components/ui/Skeleton";
import { EmptyState, ErrorState } from "../../components/ui/States";
import { Badge } from "../../components/ui/Badge";
import { formatDateTime, formatRelative } from "../../lib/format";

interface InfoItemProps {
  label: string;
  value: React.ReactNode;
}

function InfoItem({ label, value }: InfoItemProps) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs font-medium">{label}</dt>
      <dd className="mt-0.5 text-sm">{value}</dd>
    </div>
  );
}

function SnapshotCard({
  deviceId,
  metric,
  label,
}: {
  deviceId: string;
  metric: "cpu.usage_percent" | "memory.usage_percent" | "disk.usage_percent";
  label: string;
}) {
  const query = useDeviceMetrics(deviceId, metric, "1h");
  const last = query.data?.points.at(-1);

  return (
    <Card>
      <CardContent className="pt-5">
        <p className="text-muted-foreground text-xs font-medium">{label}</p>
        {query.isPending ? (
          <Skeleton className="mt-2 h-8 w-24" />
        ) : query.isError ? (
          <p className="text-danger mt-2 text-sm">Yüklenemedi</p>
        ) : (
          <p className="mt-1 text-2xl font-semibold tracking-tight">
            {last ? formatMetricValue(metric, last.value) : "—"}
          </p>
        )}
        <p className="text-muted-foreground mt-1 text-xs">Son 1 saatin güncel değeri</p>
      </CardContent>
    </Card>
  );
}

export function DeviceOverviewTab({ device }: { device: Device }) {
  const alertsQuery = useAlerts({ deviceId: device.id, perPage: 20 });
  const activeAlerts = (alertsQuery.data?.items ?? []).filter(
    (alert) => alert.state === "firing" || alert.state === "acknowledged",
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SnapshotCard deviceId={device.id} metric="cpu.usage_percent" label={metricLabel("cpu.usage_percent")} />
        <SnapshotCard deviceId={device.id} metric="memory.usage_percent" label={metricLabel("memory.usage_percent")} />
        <SnapshotCard deviceId={device.id} metric="disk.usage_percent" label={metricLabel("disk.usage_percent")} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Genel bilgiler</CardTitle>
            <CardDescription>Envanter ve kimlik bilgileri</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-4">
              <InfoItem
                label="Durum"
                value={<DeviceStatusBadge status={device.status} />}
              />
              <InfoItem
                label="İşletim sistemi"
                value={
                  <span className="flex items-center gap-2">
                    <OsBadge osFamily={device.osFamily} />
                    {[device.osName, device.osVersion].filter(Boolean).join(" ")}
                  </span>
                }
              />
              <InfoItem label="Sürüm / Build" value={device.osBuild || "—"} />
              <InfoItem label="Çekirdek" value={device.kernel || "—"} />
              <InfoItem label="Agent sürümü" value={device.agentVersion || "—"} />
              <InfoItem label="Site" value={device.site || "—"} />
              <InfoItem
                label="IP adresleri"
                value={
                  <span className="flex flex-wrap gap-1">
                    {(device.ips ?? []).map((ip) => (
                      <Badge key={ip} tone="muted">
                        {ip}
                      </Badge>
                    ))}
                    {(device.ips ?? []).length === 0 ? "—" : null}
                  </span>
                }
              />
              <InfoItem
                label="MAC adresleri"
                value={
                  <span className="flex flex-wrap gap-1">
                    {(device.macs ?? []).map((mac) => (
                      <Badge key={mac} tone="muted">
                        {mac}
                      </Badge>
                    ))}
                    {(device.macs ?? []).length === 0 ? "—" : null}
                  </span>
                }
              />
              <InfoItem label="Kayıt tarihi" value={formatDateTime(device.createdAt)} />
              <InfoItem
                label="Son görülme"
                value={`${formatDateTime(device.lastSeen)} (${formatRelative(device.lastSeen)})`}
              />
              <InfoItem
                label="Etiketler"
                value={
                  <span className="flex flex-wrap gap-1">
                    {(device.tags ?? []).map((tag) => (
                      <Badge key={tag} tone="default">
                        {tag}
                      </Badge>
                    ))}
                    {(device.tags ?? []).length === 0 ? "—" : null}
                  </span>
                }
              />
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Aktif uyarılar</CardTitle>
            <CardDescription>Bu cihaza ait tetiklenmiş/onaylanmış uyarılar</CardDescription>
          </CardHeader>
          <CardContent>
            {alertsQuery.isPending ? (
              <Skeleton className="h-40" />
            ) : alertsQuery.isError ? (
              <ErrorState
                message="Uyarılar yüklenemedi."
                onRetry={() => void alertsQuery.refetch()}
              />
            ) : activeAlerts.length === 0 ? (
              <EmptyState
                title="Aktif uyarı yok"
                description="Bu cihaz için tetiklenmiş uyarı bulunmuyor."
              />
            ) : (
              <ul className="flex flex-col gap-3">
                {activeAlerts.map((alert) => (
                  <li
                    key={alert.id}
                    className="border-border flex flex-col gap-1 rounded-md border p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">{alert.message}</span>
                      <SeverityBadge severity={alert.severity} />
                    </div>
                    <div className="text-muted-foreground flex items-center gap-2 text-xs">
                      <AlertStateBadge state={alert.state} />
                      <span>{formatRelative(alert.startedAt)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-muted-foreground mt-3 text-xs">
              Tüm uyarılar için{" "}
              <Link to={`/alerts?deviceId=${device.id}`} className="text-primary hover:underline">
                Uyarılar sayfasına
              </Link>{" "}
              bakın.
            </p>
          </CardContent>
        </Card>
      </div>

    </div>
  );
}